import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { getToken, getRefreshToken, logout, login as authLogin } from '../stores/authStore';
import { config } from '../config/environment';
import type {
  ApiResponse,
  LoginRequest,
  AuthResponse,
  Visit,
  Conversion,
  DashboardStats,
  CountryStats,
  PaginatedResponse,
  PaginationParams,
  VisitFilters,
  ConversionFilters,
} from '../types';

// Create axios instance with team-aware base URL
const api: AxiosInstance = axios.create({
  baseURL: `/api/${config.team.code}`, // Includes team code for multi-team support
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track if a refresh is already in progress to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue: Array<{ resolve: Function; reject: Function }> = [];

// Process queued requests after successful refresh
const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Request interceptor - Add auth token
api.interceptors.request.use(
  (config) => {
    let token = getToken();

    // Security: Only add Authorization if token is valid and not empty
    if (token && token.trim().length > 0) {
      // Strip Bearer prefix if it exists (shouldn't happen but safety check)
      if (token.startsWith('Bearer ')) {
        token = token.slice(7);
      }
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      // Remove Authorization header if no valid token
      delete config.headers.Authorization;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors and token refresh
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    // Handle 401 Unauthorized - attempt token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Prevent infinite loops
      originalRequest._retry = true;

      if (isRefreshing) {
        // If refresh is already in progress, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      isRefreshing = true;

      try {
        // Attempt to refresh the token
        const currentRefreshToken = getRefreshToken();
        if (!currentRefreshToken) {
          throw new Error('No refresh token available');
        }

        const refreshResponse = await axios.post(
          `/api/${config.team.code}/auth/refresh`,
          { refreshToken: currentRefreshToken },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        const newToken = refreshResponse.data.accessToken;
        const newRefreshToken = refreshResponse.data.refreshToken;

        // Update stored token and refresh token
        authLogin(newToken, undefined, newRefreshToken);

        // Process queued requests with new token
        processQueue(null, newToken);

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed - logout user
        logout();
        processQueue(refreshError, null);

        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Auth API (requires team code in URL path)
export const authApi = {
  login: async (password: string): Promise<AuthResponse> => {
    try {
      // Auth endpoint is at /api/:team/auth (team code in URL path)
      const response = await axios.post<{ is_success: boolean; token: string; accessToken?: string; refreshToken?: string }>(
        `/api/${config.team.code}/auth`,
        { password }
      );
      // Backend returns token - check both old format (token) and new format (accessToken)
      const token = response.data.accessToken || response.data.token;
      const refreshToken = response.data.refreshToken;
      return {
        token,
        refreshToken,
        message: 'Login successful',
      };
    } catch (error: any) {
      throw error;
    }
  },

  refresh: async (): Promise<AuthResponse> => {
    try {
      // Use axios directly to avoid interceptor loop, but with current credentials
      const token = getToken();
      const response = await axios.post<{ is_success: boolean; accessToken: string }>(
        `/api/${config.team.code}/auth/refresh`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        }
      );
      return {
        token: response.data.accessToken,
        message: 'Token refreshed successfully',
      };
    } catch (error: any) {
      throw error;
    }
  },

  logout: async (): Promise<ApiResponse<{ message: string }>> => {
    try {
      // Call logout endpoint on backend to revoke token
      const response = await api.post<ApiResponse<{ message: string }>>(
        `/api/${config.team.code}/auth/logout`,
        {}
      );
      return response.data;
    } catch (error: any) {
      // Even if logout fails on backend, still clear frontend token
      throw error;
    }
  },
};

// Visits API
export const visitsApi = {
  getAll: async (filters?: VisitFilters & PaginationParams): Promise<PaginatedResponse<Visit>> => {
    const response = await api.get<PaginatedResponse<Visit>>('/visits', { params: filters });
    return response.data;
  },

  getById: async (id: string): Promise<Visit> => {
    const response = await api.get<Visit>(`/visits/${id}`);
    return response.data;
  },

  getByClickId: async (clickId: string): Promise<Visit[]> => {
    const response = await api.get<Visit[]>(`/visits/click/${clickId}`);
    return response.data;
  },

  getStats: async (filters?: VisitFilters): Promise<DashboardStats> => {
    const response = await api.get<DashboardStats>('/visits/stats', { params: filters });
    return response.data;
  },
};

// Conversions API
export const conversionsApi = {
  getAll: async (
    filters?: ConversionFilters & PaginationParams
  ): Promise<PaginatedResponse<Conversion>> => {
    const response = await api.get<PaginatedResponse<Conversion>>('/conversions', {
      params: filters,
    });
    return response.data;
  },

  getById: async (id: string): Promise<Conversion> => {
    const response = await api.get<Conversion>(`/conversions/${id}`);
    return response.data;
  },

  getByClickId: async (clickId: string): Promise<Conversion | null> => {
    const response = await api.get<Conversion | null>(`/conversions/click/${clickId}`);
    return response.data;
  },

  create: async (data: Omit<Conversion, 'id' | 'created_at'>): Promise<Conversion> => {
    const response = await api.post<Conversion>('/conversions', data);
    return response.data;
  },
};

// Statistics API
export const statsApi = {
  getDashboard: async (startDate?: string, endDate?: string): Promise<DashboardStats> => {
    const response = await api.get<DashboardStats>('/stats/dashboard', {
      params: { startDate, endDate },
    });
    return response.data;
  },

  getByCountry: async (startDate?: string, endDate?: string): Promise<CountryStats[]> => {
    const response = await api.get<CountryStats[]>('/stats/countries', {
      params: { startDate, endDate },
    });
    return response.data;
  },

  getClicksChart: async (
    period: 'day' | 'week' | 'month',
    startDate?: string,
    endDate?: string
  ) => {
    const response = await api.get('/stats/clicks-chart', {
      params: { period, startDate, endDate },
    });
    return response.data;
  },

  getConversionsChart: async (
    period: 'day' | 'week' | 'month',
    startDate?: string,
    endDate?: string
  ) => {
    const response = await api.get('/stats/conversions-chart', {
      params: { period, startDate, endDate },
    });
    return response.data;
  },

  getReport: async (startDate?: string, endDate?: string): Promise<any[]> => {
    const response = await api.get<any[]>('/stats/report', {
      params: { startDate, endDate },
    });
    return response.data;
  },

  getUserReport: async (userId: string, startDate?: string, endDate?: string): Promise<any[]> => {
    const response = await api.get<any[]>(`/stats/report/user/${encodeURIComponent(userId)}`, {
      params: { startDate, endDate },
    });
    return response.data;
  },
};

// Export the axios instance for custom requests
export default api;
